"use client"
import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Copy, Check, MessageCircle, Instagram, Facebook, Twitter, Mail } from "lucide-react"
import { toast } from "sonner";
import {useLogger} from "@/lib/use-logger";
import {useTranslations} from "next-intl";

interface SocialPlatform {
  name: string
  icon: string
  color: string
  utmSource: string
}

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description: string
  linkLabel: string
  linkPlaceholder: string
  socialPlatforms: SocialPlatform[]
  shareMessage: string
  utmCampaign: string
}

const iconMap = {
  "message-circle": MessageCircle,
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  mail: Mail,
}

export function ShareModal({
  isOpen,
  onClose,
  title,
  description,
  linkLabel,
  linkPlaceholder,
  socialPlatforms,
  shareMessage,
  utmCampaign,
}: ShareModalProps) {
  const log = useLogger(import.meta.url);
  const t = useTranslations("common")
  const [isCopied, setIsCopied] = useState(false)
  const [instagramCopied, setInstagramCopied] = useState(false)
  const effectiveIsCopied = isOpen && isCopied
  const effectiveInstagramCopied = isOpen && instagramCopied

  const createUTMLink = (utmSource: string) => {
    const baseUrl = linkPlaceholder
    const utmParams = new URLSearchParams({
      utm_source: utmSource,
      utm_medium: "referral",
      utm_campaign: utmCampaign,
    })
    return `${baseUrl}?${utmParams.toString()}`
  }

  const handleCopy = async () => {
    try {
      // Copy the UTM version with "copy" as source
      const utmLink = createUTMLink("copy")
      await navigator.clipboard.writeText(utmLink)
      setIsCopied(true)
      toast.success(t("shareModal.linkCopied"))
      // Reset after 3 seconds
      setTimeout(() => setIsCopied(false), 3000)
    } catch {
      toast.error(t("shareModal.copyFailed"))
    }
  }

  const handleSocialShare = async (platform: SocialPlatform) => {
    const utmLink = createUTMLink(platform.utmSource)
    const fullMessage = `${shareMessage} ${utmLink}`

    let shareUrl = ""

    switch (platform.utmSource) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(fullMessage)}`
        break
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(utmLink)}`
        break
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(utmLink)}`
        break
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent("🌟 Mach mit bei donista!")}&body=${encodeURIComponent(fullMessage)}`
        break
      case "instagram":
        // Instagram doesn't support direct link sharing, so we copy to clipboard
        try {
          await navigator.clipboard.writeText(fullMessage)
          setInstagramCopied(true)

          toast.success(t("shareModal.instagramCopied"))

          // Reset after 3 seconds
          setTimeout(() => setInstagramCopied(false), 3000)
        } catch {
          toast.error(t("shareModal.copyFailed"))
        }
        return
    }

    if (shareUrl) {
      log.debug(shareUrl)
      window.open(shareUrl, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">{description}</p>

          <div className="space-y-2">
            <Label htmlFor="invite-link">{linkLabel}</Label>
            <div className="flex gap-2">
              <Input id="invite-link" value={linkPlaceholder} readOnly className="flex-1" />
              <Button
                variant={effectiveIsCopied ? "default" : "outline"}
                size="icon"
                onClick={handleCopy}
              >
                {effectiveIsCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <Label>{t("shareModal.directShare")}</Label>
            <div className="grid grid-cols-1 gap-3">
              {socialPlatforms.map((platform) => {
                const IconComponent = iconMap[platform.icon as keyof typeof iconMap]
                const isInstagram = platform.utmSource === "instagram"
                const isInstagramCopied = isInstagram && effectiveInstagramCopied

                return (
                  <Button
                    key={platform.name}
                    variant={isInstagramCopied ? "default" : "outline"}
                    className={`h-12 justify-start gap-3`}
                    onClick={() => handleSocialShare(platform)}
                  >
                    <div className={`w-6 h-6 rounded-full ${platform.color} flex items-center justify-center`}>
                      <IconComponent className="w-4 h-4 text-white" />
                    </div>
                    {platform.name}
                    {isInstagram && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {isInstagramCopied ? (
                          <span className="flex items-center gap-1 text-white">
                            <Check className="w-3 h-3" />
                            {t("actions.copied")}
                          </span>
                        ) : (
                          t("shareModal.linkWillBeCopied")
                        )}
                      </span>
                    )}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Export types for use in other components
export type { SocialPlatform }